import { apiRequest } from '../../../shared/services/apiClient';
import type {
  BillingAddon,
  BillingCheckout,
  BillingPaymentProvider,
  BillingPaymentProviderCode,
  BillingPlan,
  BillingPlanCode,
  BillingStatus,
  WorkspaceBilling,
} from '../types/billing';

const PLAN_CODES: BillingPlanCode[] = ['starter', 'growth', 'advanced', 'enterprise'];
const STATUSES: BillingStatus[] = ['unselected', 'trial', 'active', 'trialEnded', 'pastDue'];

const toCount = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toPlanCode = (value: unknown): BillingPlanCode | null => {
  const code = String(value ?? '').trim().toLowerCase();
  return PLAN_CODES.includes(code as BillingPlanCode) ? (code as BillingPlanCode) : null;
};

const toStatus = (value: unknown): BillingStatus => {
  const status = String(value ?? '').trim();
  return STATUSES.includes(status as BillingStatus) ? (status as BillingStatus) : 'unselected';
};

const normalizePlan = (value: any): BillingPlan => ({
  code: toPlanCode(value?.code) ?? 'starter',
  name: String(value?.name ?? ''),
  monthlyPriceUsd: toCount(value?.monthlyPriceUsd),
  monthlyPriceAzn: toCount(value?.monthlyPriceAzn),
  includedUsers: toCount(value?.includedUsers),
  includedActiveContactsPerMonth: toCount(value?.includedActiveContactsPerMonth),
  highlighted: Boolean(value?.highlighted),
  featureKeys: Array.isArray(value?.featureKeys)
    ? value.featureKeys.map((item: unknown) => String(item ?? '').trim()).filter(Boolean)
    : [],
  enabledFeatures: Array.isArray(value?.enabledFeatures)
    ? value.enabledFeatures.map((item: unknown) => String(item ?? '').trim()).filter(Boolean)
    : [],
});

const normalizeAddon = (value: any): BillingAddon => ({
  code: String(value?.code ?? ''),
  monthlyPriceUsd: toCount(value?.monthlyPriceUsd),
  priceNoteKey: String(value?.priceNoteKey ?? ''),
});

const normalizePaymentProvider = (value: any): BillingPaymentProvider => ({
  code: String(value?.code ?? '').trim().toLowerCase() as BillingPaymentProviderCode,
  currency: String(value?.currency ?? '').trim().toUpperCase(),
});

const normalizeWorkspace = (value: any): WorkspaceBilling => ({
  status: toStatus(value?.status),
  planCode: toPlanCode(value?.planCode),
  selectedPlanCode: toPlanCode(value?.selectedPlanCode),
  currentPeriodEndUtc: value?.currentPeriodEndUtc ? String(value.currentPeriodEndUtc) : null,
  trialEndsAtUtc: value?.trialEndsAtUtc ? String(value.trialEndsAtUtc) : null,
  trialDaysRemaining: toCount(value?.trialDaysRemaining),
  trialAvailable: Boolean(value?.trialAvailable),
  checkoutEnabled: Boolean(value?.checkoutEnabled),
  currency: String(value?.currency ?? 'AZN'),
  paymentProviders: Array.isArray(value?.paymentProviders)
    ? value.paymentProviders.map(normalizePaymentProvider).filter(Boolean)
    : [],
  selectedAtUtc: value?.selectedAtUtc ? String(value.selectedAtUtc) : null,
  plans: Array.isArray(value?.plans) ? value.plans.map(normalizePlan) : [],
  addons: Array.isArray(value?.addons) ? value.addons.map(normalizeAddon) : [],
  contactEmail: String(value?.contactEmail ?? 'info@simaizeer.org'),
  contactPhone: String(value?.contactPhone ?? '+994505551942'),
  entitlements: {
    features: Array.isArray(value?.entitlements?.features)
      ? value.entitlements.features.map((item: unknown) => String(item ?? '').trim()).filter(Boolean)
      : [],
    includedUsers: toCount(value?.entitlements?.includedUsers),
    includedActiveContactsPerMonth: toCount(value?.entitlements?.includedActiveContactsPerMonth),
  },
  entitlementsKnown: value?.entitlements != null && typeof value.entitlements === 'object',
  plansHidden: Boolean(value?.plansHidden),
  canEditFeatures: Boolean(value?.canEditFeatures),
});

const normalizeCheckout = (value: any): BillingCheckout => ({
  orderId: String(value?.orderId ?? ''),
  paymentUrl: String(value?.paymentUrl ?? ''),
  amountAzn: toCount(value?.amountAzn),
  provider: String(value?.provider ?? ''),
  amount: toCount(value?.amount),
  currency: String(value?.currency ?? ''),
});

export const getWorkspaceBilling = async (): Promise<WorkspaceBilling> => {
  const result = await apiRequest<any>('/api/v1/billing');
  return normalizeWorkspace(result);
};

// Enterprise is agreed with sales, so it is recorded without a payment. Paid plans use checkout.
export const selectBillingPlan = async (planCode: BillingPlanCode): Promise<WorkspaceBilling> => {
  const result = await apiRequest<any>('/api/v1/billing/select', {
    method: 'POST',
    body: JSON.stringify({ planCode }),
  });
  return normalizeWorkspace(result);
};

export const startBillingTrial = async (): Promise<WorkspaceBilling> => {
  const result = await apiRequest<any>('/api/v1/billing/trial', { method: 'POST' });
  return normalizeWorkspace(result);
};

export const setPlanFeature = async (
  planCode: BillingPlanCode,
  featureKey: string,
  enabled: boolean,
): Promise<WorkspaceBilling> => {
  const result = await apiRequest<any>(`/api/v1/billing/plans/${encodeURIComponent(planCode)}/features`, {
    method: 'PUT',
    body: JSON.stringify({ featureKey, enabled }),
  });
  return normalizeWorkspace(result);
};

export const setPlansVisibility = async (hidden: boolean): Promise<WorkspaceBilling> => {
  const result = await apiRequest<any>('/api/v1/billing/plans-visibility', {
    method: 'PUT',
    body: JSON.stringify({ hidden }),
  });
  return normalizeWorkspace(result);
};

export const extendAccessUntil = async (accessUntil: string): Promise<WorkspaceBilling> => {
  const result = await apiRequest<any>('/api/v1/billing/access-until', {
    method: 'PUT',
    body: JSON.stringify({ accessUntil }),
  });
  return normalizeWorkspace(result);
};

// The provider is omitted when the workspace is offered only one, and the API falls back to it.
export const startBillingCheckout = async (
  planCode: BillingPlanCode,
  provider?: BillingPaymentProviderCode,
): Promise<BillingCheckout> => {
  const result = await apiRequest<any>('/api/v1/billing/checkout', {
    method: 'POST',
    body: JSON.stringify(provider ? { planCode, provider } : { planCode }),
  });
  return normalizeCheckout(result);
};
